import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Paperclip, Plus } from "lucide-react";
import { api } from "../lib/api";
import { fmtDate, isAdmin } from "../lib/roles";
import { useAuth } from "../context/AuthContext";
import { usePerm } from "../context/PermContext";
import { MyShiftsTable } from "../components/MyShiftsTable";
import { WeekShifts } from "../components/WeekShifts";
import { OnShiftWithMe } from "../components/OnShiftWithMe";
import { Empty, Loading, PageHeader, Panel, StatoBadge } from "../components/common";
import { Button } from "../components/ui/button";

const leaveRange = (l) => l.ora_inizio ? `Il ${fmtDate(l.data_inizio)}, ${l.ora_inizio} - ${l.ora_fine}` : `Dal ${fmtDate(l.data_inizio)} al ${fmtDate(l.data_fine)}`;
const H2 = ({ children }) => <h2 className="font-display text-xl font-bold text-[var(--navy)] mb-4">{children}</h2>;

const Leaves = ({ leaves }) => (
  <div>
    <H2>Ferie, permessi e L.104</H2>
    {leaves.length === 0 ? <Empty title="Nessuna richiesta" text="Invia una richiesta di ferie o permesso." /> : (
      <div className="space-y-3" data-testid="my-leaves-list">{leaves.map((l) => (
        <Panel key={l.id} className="!p-4">
          <div className="flex items-center justify-between mb-1"><span className="font-semibold text-[var(--navy)]">{l.tipo}</span><StatoBadge stato={l.stato} /></div>
          <p className="text-sm text-slate-600">{leaveRange(l)}</p>
          {l.motivo && <p className="text-sm text-slate-500 mt-1">{l.motivo}</p>}
          {l.has_allegato && <p className="text-xs text-[var(--teal)] mt-2 flex items-center gap-1"><Paperclip className="w-3 h-3" />Certificato allegato</p>}
          {l.nota_admin && <p className="text-xs text-slate-500 mt-2">Nota: {l.nota_admin}</p>}
        </Panel>))}
      </div>)}
  </div>
);

const Overtime = ({ overtime }) => (
  <div>
    <H2>Straordinari del mese</H2>
    {overtime.length === 0 ? <Empty title="Nessuno straordinario" testid="overtime-empty" /> : (
      <Panel className="!p-0" data-testid="overtime-list">{overtime.map((o, i) => (
        <div key={i} className="flex justify-between px-5 py-3 border-b last:border-0 text-sm"><span>{fmtDate(o.data)}</span><span className="font-semibold">{o.ore_tot ?? 0} h{o.ore_notte ? ` (notte ${o.ore_notte} h)` : ""}</span></div>))}
      </Panel>)}
  </div>
);

export default function Turni() {
  const { can } = usePerm();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [d, setD] = useState(null);
  useEffect(() => {
    const now = new Date();
    Promise.all([api("/shifts/me"), api("/leaves/me"), api(`/overtime/me?mese=${now.getMonth() + 1}&anno=${now.getFullYear()}`)].map((p) => p.catch(() => ({}))))
      .then(([s, l, o]) => setD({ shifts: s.shifts || [], leaves: l.leaves || [], overtime: o.overtime || [] }));
  }, []);
  if (!d) return <Loading />;
  return (
    <div>
      <PageHeader subtitle="Turni" title="Turni, Ferie e permessi" actions={can("turni.ferie") && <Button asChild className="rounded-full bg-[var(--teal)] hover:bg-[#257474]" data-testid="new-leave-button"><Link to="/ferie/new"><Plus className="w-4 h-4 mr-1" />Richiedi ferie o permesso</Link></Button>} />
      <div className="space-y-10">
        {can("turni.view") && (<>
          <OnShiftWithMe heading={<H2>Chi è di turno con me oggi</H2>} />
          <section data-testid="week-shifts-section"><H2>I miei turni della settimana</H2><WeekShifts shifts={d.shifts} /></section>
          <section className="min-w-0"><H2>Turni del reparto</H2><MyShiftsTable shifts={d.shifts} admin={admin} /></section>
        </>)}
        {(can("turni.ferie") || can("turni.straordinari")) && (
          <div className="grid lg:grid-cols-2 gap-8">
            {can("turni.ferie") && <Leaves leaves={d.leaves} />}
            {can("turni.straordinari") && <Overtime overtime={d.overtime} />}
          </div>)}
      </div>
    </div>
  );
}
